// Full-transcript card with optional "Re-transcribe with Whisper" override
// for master admins. The button is hidden when the caller doesn't pass
// `canRetranscribe` (e.g. non-admin users).
import React, { useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mic, Copy, Download, Search, ChevronUp, ChevronDown, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { cleanTranscript, parseTranscriptLines } from './callDetailUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Render one line's text with `<mark>` wrappers around search hits. The
// currently-active match gets a stronger colour + a data attribute so we
// can scroll it into view.
const renderHighlighted = (lineInfo, activeGlobalIdx) => {
  const nodes = [];
  let matchCounter = 0;
  lineInfo.parts.forEach((piece, i) => {
    if (i % 2 === 1) {
      const m = lineInfo.matches[matchCounter++];
      const active = m && m.globalIdx === activeGlobalIdx;
      nodes.push(
        <mark
          key={i}
          data-match-idx={m ? m.globalIdx : undefined}
          className={active
            ? 'bg-amber-400 text-slate-900 rounded px-0.5 ring-2 ring-amber-500'
            : 'bg-yellow-200 text-slate-900 rounded px-0.5'}
        >
          {piece}
        </mark>,
      );
    } else if (piece) {
      nodes.push(<React.Fragment key={i}>{piece}</React.Fragment>);
    }
  });
  return nodes;
};

export const TranscriptionCard = ({
  transcription,
  callId,
  canRetranscribe = false,
  onUpdated,
  agentId,
  agentName,
  domainName,
  customerPhone,
}) => {
  const [busy, setBusy] = useState(false);
  const [localText, setLocalText] = useState(null);
  const [query, setQuery] = useState('');
  const [activeMatch, setActiveMatch] = useState(0); // 0-indexed
  const containerRef = useRef(null);

  const text = localText ?? transcription;
  const cleanText = cleanTranscript(text);
  const parsedLines = useMemo(
    () => parseTranscriptLines(cleanText, { agentId, agentName, domainName, customerPhone }),
    [cleanText, agentId, agentName, domainName, customerPhone],
  );

  // Search index — build a flat list of matches with a stable key so we can
  // wire "prev / next" navigation and scroll-into-view the active one.
  const searchInfo = useMemo(() => {
    const q = query.trim();
    if (!q) return { total: 0, byLine: [] };
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    let total = 0;
    const byLine = parsedLines.map((line, li) => {
      const parts = line.text.split(re);
      const matches = [];
      parts.forEach((piece, pi) => {
        if (pi % 2 === 1) matches.push({ globalIdx: total + matches.length });
      });
      total += matches.length;
      return { parts, matches };
    });
    return { total, byLine };
  }, [query, parsedLines]);

  // Scroll the active match into view whenever it (or the query) changes
  useEffect(() => {
    if (!query || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-match-idx="${activeMatch}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeMatch, query]);

  useEffect(() => { setActiveMatch(0); }, [query]);

  const copyToClipboard = async () => {
    if (!cleanText) return;
    try {
      await navigator.clipboard.writeText(cleanText);
      toast.success('Transcript copied to clipboard');
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  const downloadTxt = () => {
    if (!cleanText) return;
    const blob = new Blob([cleanText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${callId || 'call'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const runWhisper = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/calls/${callId}/whisper-retranscribe`,
      );
      setLocalText(res.data.transcription || '');
      toast.success(
        `Whisper re-transcribed (${res.data.chars || 0} chars)`,
      );
      if (typeof onUpdated === 'function') onUpdated();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || 'Whisper re-transcribe failed';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="transcription-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Transcription</CardTitle>
          <CardDescription>Full call recording transcript</CardDescription>
        </div>
        {canRetranscribe && callId && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              disabled={!cleanText}
              data-testid="transcript-copy-btn"
              title="Copy transcript to clipboard"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTxt}
              disabled={!cleanText}
              data-testid="transcript-download-btn"
              title="Download transcript as .txt"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download
            </Button>
            <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                data-testid="retranscribe-whisper-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mic className="w-4 h-4 mr-2" />
                )}
                {busy ? 'Transcribing…' : 'Re-transcribe with Whisper'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="retranscribe-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Re-transcribe this call?</AlertDialogTitle>
                <AlertDialogDescription>
                  This overwrites the existing transcript using OpenAI Whisper on
                  the call recording. It consumes Emergent LLM credits and can
                  take up to a minute for long calls.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="retranscribe-cancel-btn">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={runWhisper}
                  data-testid="retranscribe-confirm-btn"
                >
                  Run Whisper
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        )}
        {!canRetranscribe && callId && cleanText && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              data-testid="transcript-copy-btn"
              title="Copy transcript to clipboard"
            >
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTxt}
              data-testid="transcript-download-btn"
              title="Download transcript as .txt"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Search bar */}
        {cleanText && (
          <div className="mb-3 flex items-center gap-2" data-testid="transcript-search-bar">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search transcript…"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-100 focus:outline-none"
                data-testid="transcript-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {query && (
              <>
                <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap" data-testid="transcript-match-count">
                  {searchInfo.total === 0 ? 'No matches' : `${activeMatch + 1} / ${searchInfo.total}`}
                </span>
                <button
                  type="button"
                  disabled={searchInfo.total === 0}
                  onClick={() => setActiveMatch((i) => (i - 1 + searchInfo.total) % searchInfo.total)}
                  className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                  title="Previous match"
                  data-testid="transcript-match-prev"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={searchInfo.total === 0}
                  onClick={() => setActiveMatch((i) => (i + 1) % searchInfo.total)}
                  className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                  title="Next match"
                  data-testid="transcript-match-next"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Colour-coded lines */}
        <div
          ref={containerRef}
          className="transcript-viewer space-y-1.5"
          data-testid="transcription-text"
        >
          {parsedLines.length === 0 ? (
            <p className="text-slate-500 italic text-sm">No transcript available.</p>
          ) : (
            parsedLines.map((line, i) => {
              const lineMatches = searchInfo.byLine[i];
              const isAgent = line.isAgent;
              // Colour band on the left + tint the bubble
              const bandCls = isAgent ? 'bg-teal-500' : 'bg-amber-500';
              const bubbleCls = isAgent
                ? 'bg-teal-50/60 border-teal-100'
                : 'bg-amber-50/60 border-amber-100';
              const speakerCls = isAgent ? 'text-teal-700' : 'text-amber-700';
              return (
                <div
                  key={i}
                  className={`flex gap-2 rounded-md border ${bubbleCls} pl-1.5 pr-3 py-1.5`}
                  data-testid={`transcript-line-${i}`}
                  data-speaker={isAgent ? 'agent' : 'customer'}
                >
                  <div className={`w-0.5 shrink-0 rounded-full ${bandCls}`} />
                  <div className="flex-1 min-w-0">
                    {line.speaker && (
                      <div className={`text-[10px] font-semibold uppercase tracking-wider ${speakerCls} mb-0.5`}>
                        {isAgent ? 'Agent' : 'Caller'} · {line.speaker}
                      </div>
                    )}
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                      {query && lineMatches
                        ? renderHighlighted(lineMatches, activeMatch)
                        : line.text}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};
